import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, X, Flame, Hash, Crown, Check, Lock } from 'lucide-react';
import { api, parseApiError, type Me, type MyCardResponse, type MyCardUpdateInput, type MyCardModeStats } from '../lib/api';
import { HelperCard } from '../components/HelperCard';
import { PageSpinner } from '../components/Spinner';
import { EmptyState } from '../components/EmptyState';
import { PageWrapper } from '../components/PageWrapper';
import { CoverImage } from '../components/CoverImage';
import { FocalPointPicker } from '../components/FocalPointPicker';
import { SaveBar } from '../components/SaveBar';
import { useEffectiveRole } from '../lib/viewAs';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';

// ─────────────────────────────────────────────────────────────────────────────
// MyCard — the user's own player card with edit mode.
//
// Phase 2 of the My Card / My Org / Role Requests loop. Reads GET /me/card,
// writes PUT /me/card (field-level UpdateExpression on the existing
// UserProfile row). View mode by default; click Edit to surface a form.
//
// Editable: gamertag, displayName, description, cardColor, socialLinks
// (discord/twitter/twitch). Avatar/banner upload deferred — there's no
// existing uploader UI for player-card assets; that's a follow-up.
//
// Read-only: lifetime stats (sourced from match submissions), Discord
// enrichment, updatedAt.
// ─────────────────────────────────────────────────────────────────────────────

// Vibrant accent presets — these drive the WHOLE-APP theme palette
// (not the card plate). Pick one and the entire UI shifts to shades of
// it. Distinct from PRESET_COLORS which are dark surface colours.
const ACCENT_PRESETS = [
  '#5b0f6e', // deep purple
  '#7c3aed', // violet
  '#0ea5e9', // sky blue
  '#14b8a6', // teal
  '#10b981', // emerald
  '#f59e0b', // amber gold
  '#ef4444', // crimson
  '#ec4899', // pink
  '#ff731b', // forge orange (brand)
];

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

type FormState = {
  gamertag: string;
  displayName: string;
  description: string;
  cardColor: string;
  accentOverride: string;
  tiktok: string;
  youtube: string;
  discord: string;
  twitter: string;
  twitch: string;
  bannerObjectPosition: string;
};

function cardToFormState(
  card: MyCardResponse,
  accentOverride: string | null = null,
  discordUsernameFallback: string | null = null,
): FormState {
  return {
    gamertag: card.gamertag ?? '',
    displayName: card.displayName ?? '',
    description: card.description ?? '',
    cardColor: card.cardColor ?? '',
    accentOverride: accentOverride ?? '',
    tiktok: card.socialLinks?.tiktok ?? '',
    youtube: card.socialLinks?.youtube ?? '',
    // Pre-populate Discord from the user's discordUsername when no value
    // has been saved yet. Once they save (even to empty), respect that.
    discord: card.socialLinks?.discord ?? discordUsernameFallback ?? '',
    twitter: card.socialLinks?.twitter ?? '',
    twitch: card.socialLinks?.twitch ?? '',
    bannerObjectPosition: card.bannerObjectPosition ?? '',
  };
}

// Write-once lock (#294): the backend stamps gamertagLockedAt on the FIRST
// successful gamertag set; after that, a player's own PUT /me/card 403s if
// it touches gamertag (only an admin's PATCH /admin/users/:id/gamertag can
// change it — a separate, not-yet-built admin surface). Absence of the
// field — never set yet, or a legacy profile from before this shipped —
// reads as unlocked, matching the backend's own phase-in rule.
export function isGamertagLocked(card: MyCardResponse): boolean {
  return !!card.gamertagLockedAt;
}

// Build a minimal PUT payload — only include keys the user actually changed.
// Empty string -> null (clears the field). Untouched fields are omitted so
// the field-level UpdateExpression doesn't touch them.
//
// gamertagLocked defaults to false but MUST be passed true whenever the
// card is locked — the gamertag input is disabled once locked, so this is
// belt-and-braces: it guarantees a stray/rehydrated form value can never
// ride along in the payload and trip the backend's 403.
export function diffFormState(initial: FormState, current: FormState, gamertagLocked = false): MyCardUpdateInput {
  const out: MyCardUpdateInput = {};

  const stringField = (key: keyof FormState, target: keyof MyCardUpdateInput) => {
    if (initial[key] === current[key]) return;
    const v = current[key];
    (out as Record<string, string | null>)[target as string] = v === '' ? null : v;
  };

  if (!gamertagLocked) stringField('gamertag', 'gamertag');
  stringField('displayName', 'displayName');
  stringField('description', 'description');
  stringField('bannerObjectPosition', 'bannerObjectPosition');

  if (initial.cardColor !== current.cardColor) {
    out.cardColor = current.cardColor === '' ? null : current.cardColor;
  }

  if (initial.accentOverride !== current.accentOverride) {
    out.accentOverride = current.accentOverride === '' ? null : current.accentOverride;
  }

  // Social links — submit a partial object only if any of the five changed.
  const socialChanged =
    initial.tiktok !== current.tiktok ||
    initial.youtube !== current.youtube ||
    initial.discord !== current.discord ||
    initial.twitter !== current.twitter ||
    initial.twitch !== current.twitch;
  if (socialChanged) {
    out.socialLinks = {
      ...(initial.tiktok !== current.tiktok ? { tiktok: current.tiktok === '' ? null : current.tiktok } : {}),
      ...(initial.youtube !== current.youtube ? { youtube: current.youtube === '' ? null : current.youtube } : {}),
      ...(initial.discord !== current.discord ? { discord: current.discord === '' ? null : current.discord } : {}),
      ...(initial.twitter !== current.twitter ? { twitter: current.twitter === '' ? null : current.twitter } : {}),
      ...(initial.twitch !== current.twitch ? { twitch: current.twitch === '' ? null : current.twitch } : {}),
    };
  }

  return out;
}

function validate(form: FormState): string | null {
  if (form.gamertag.trim().length > 50) return 'Gamertag must be 50 chars or fewer.';
  if (form.displayName.trim().length > 50) return 'Display name must be 50 chars or fewer.';
  if (form.description.length > 500) return 'Description must be 500 chars or fewer.';
  if (form.cardColor && !HEX_RE.test(form.cardColor)) return 'Card colour must be a 6-digit hex code (e.g. #1a1a24).';
  if (form.accentOverride && !HEX_RE.test(form.accentOverride)) return 'Accent colour must be a 6-digit hex code (e.g. #5b0f6e).';
  for (const [label, value] of [['TikTok', form.tiktok], ['YouTube', form.youtube], ['Discord', form.discord], ['Twitter', form.twitter], ['Twitch', form.twitch]] as const) {
    if (!value) continue;
    if (value.length > 200) return `${label} link must be 200 chars or fewer.`;
    if (/^https?:\/\//i.test(value)) {
      try {
        const u = new URL(value);
        if (u.protocol !== 'http:' && u.protocol !== 'https:') return `${label} link must use http(s).`;
      } catch {
        return `${label} link is not a valid URL.`;
      }
    } else if (/\s/.test(value)) {
      return `${label} handle must not contain whitespace.`;
    }
  }
  return null;
}

function MyCardIntro({ className }: { className?: string }) {
  const { data: me } = useQuery<Me>({ queryKey: ['me'], queryFn: api.me });
  const role = useEffectiveRole(me?.role);
  const isFounder = role === 'founder';
  const isAdmin = role === 'admin';

  const intro = isFounder
    ? "Founder view: this is your public card. Stats are sourced from real match submissions; cosmetics are yours to set. Switch ‘view as’ bottom-left to see what other roles see."
    : isAdmin
    ? "Your public card — what other players see when your name shows up on a match. Stats reflect every match you’ve been on; cosmetics are yours."
    : "This is your public card. Other players see this when your name shows up on a match scoreboard. Stats are tracked automatically; tweak your gamertag, colour, and links to make it yours.";

  const points = [
    { text: 'Edit gamertag, display name, description, card colour, social links.' },
    { text: 'Lifetime stats are read-only — they come from real OCR’d match submissions, not self-report.' },
    { text: 'Avatar / banner upload is queued; coming with the asset manager pass.' },
  ];

  return <HelperCard id="mycard-intro" title="What lives here" intro={intro} points={points} className={className} />;
}

export function MyCard() {
  const qc = useQueryClient();
  const { data: card, isLoading, error } = useQuery({
    queryKey: ['myCard'],
    queryFn: api.myCard,
    retry: false,
  });

  // Pull the rich Discord profile (avatar, accent, banner, clan tag) from
  // /me — separate endpoint to /me/card. The card hero uses these for
  // the actual Discord avatar + accent ring instead of the monogram
  // fallback, and renders the user's profile banner image as a strip
  // behind the hero when one is set.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  const discordProfile = me?.discordProfile;

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  // Lifetime stats view — cumulative totals vs per-game averages (total ÷ games).
  const [statView, setStatView] = useState<'total' | 'avg'>('total');

  // Whenever a fresh card lands while not editing, snap form to it.
  useEffect(() => {
    if (card && !editing) {
      setForm(cardToFormState(
        card,
        discordProfile?.accentOverride ?? null,
        discordProfile?.globalName ?? card.discordUsername ?? null,
      ));
    }
  }, [card, editing, discordProfile?.accentOverride, discordProfile?.globalName]);

  useEffect(() => {
    document.title = card?.displayName
      ? `${card.displayName} · My Card · GG Anvil`
      : 'My Card · GG Anvil';
  }, [card?.displayName]);

  const initialForm = useMemo(
    () => (card
      ? cardToFormState(
          card,
          discordProfile?.accentOverride ?? null,
          discordProfile?.globalName ?? card.discordUsername ?? null,
        )
      : null),
    [card, discordProfile?.accentOverride, discordProfile?.globalName],
  );

  const mutation = useMutation({
    mutationFn: (input: MyCardUpdateInput) => api.updateMyCard(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['myCard'] });
      qc.invalidateQueries({ queryKey: ['me'] });
      setEditing(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    },
    onError: (e: Error) => setFormError(parseApiError(e).message || 'Save failed.'),
  });

  if (isLoading) return <PageSpinner />;

  if (error) {
    return (
      <EmptyState
        title="Couldn't load your card"
        message={(error as Error).message || 'Try reloading the page.'}
      />
    );
  }
  if (!card) return null;

  // Not initialised = no PROFILE row yet (user hasn't completed POPIA consent
  // or the consent bootstrap hasn't run). Surface the situation rather than
  // crash — the user has a valid JWT to be here at all.
  if (!card.initialised) {
    return (
      <div className="mx-auto max-w-2xl">
        <EmptyState
          title="Profile not set up yet"
          message="Your player profile hasn't been bootstrapped. Run /ggportal in Discord to complete the consent + profile flow, then come back here."
        />
      </div>
    );
  }

  const onSave = () => {
    if (!form || !initialForm) return;
    setFormError(null);
    const validation = validate(form);
    if (validation) {
      setFormError(validation);
      return;
    }
    const diff = diffFormState(initialForm, form, isGamertagLocked(card));
    if (Object.keys(diff).length === 0) {
      setEditing(false);
      return;
    }
    mutation.mutate(diff);
  };

  const onCancel = () => {
    setForm(initialForm);
    setFormError(null);
    setEditing(false);
  };

  const gamertagLocked = isGamertagLocked(card);
  const dirty = !!(form && initialForm && Object.keys(diffFormState(initialForm, form, gamertagLocked)).length > 0);
  const cardColor = (form?.cardColor && HEX_RE.test(form.cardColor)) ? form.cardColor : (card.cardColor || '#1a1a24');
  const displayName = card.displayName || card.discordUsername || 'Player';
  const monogram = (displayName?.[0] ?? '?').toUpperCase();

  return (
    <PageWrapper maxWidth="2xl" spacing="none" className="relative pb-16">
      {/* Brand strip */}
      <div className={cn('mb-5 flex items-end justify-between px-1', SETTLE)} style={settleDelay(0)}>
        <div className="flex items-center gap-2 opacity-85">
          <img src="/brand/gganvil-mark.svg" alt="" className="h-5 w-5" />
          <span className="font-display text-[10px] font-black uppercase tracking-[0.3em] text-ink-meta">
            GG Anvil · My Card
          </span>
        </div>
        <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-ink-meta">
          № {card.discordId.slice(-6)}
        </span>
      </div>

      <div className={SETTLE} style={settleDelay(1)}>
        <MyCardIntro className="mb-5" />
      </div>

      {/* Plate */}
      <article
        className={cn('plate relative px-6 py-8 sm:px-10 sm:py-10', SETTLE)}
        style={{ ...settleDelay(2), backgroundColor: cardColor }}
      >
        {/* Top bar */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-ink-secondary">
              Personal Record
            </p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-meta">
              {card.updatedAt
                ? `Updated ${new Date(card.updatedAt).toLocaleDateString('en-GB')}`
                : 'Pending first edit'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="seal shrink-0">
              <Flame className="h-3 w-3" strokeWidth={3} />
              You
            </span>
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                className={cn('btn-ghost inline-flex items-center gap-2', MORPH)}
              >
                <Pencil className="h-3 w-3" strokeWidth={3} />
                Edit
              </button>
            ) : (
              <button
                onClick={onCancel}
                className={cn('btn-ghost inline-flex items-center gap-2 hover:text-red-300', MORPH)}
              >
                <X className="h-3 w-3" strokeWidth={3} />
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* Saved-flash */}
        {savedFlash && (
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-white/[0.04] px-3 py-2">
            <Check className="h-3.5 w-3.5 shrink-0 text-emerald-400" strokeWidth={3} />
            <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
              Saved
            </span>
          </div>
        )}

        {/* Discord profile banner strip — rendered behind the hero when the
            user has set one in their Discord profile. Falls back to a thin
            accent stripe derived from their bannerColor (or accentColor). */}
        {discordProfile?.banner && (
          <div className="-mx-6 sm:-mx-10 mt-4 h-24 overflow-hidden">
            <CoverImage
              src={`https://cdn.discordapp.com/banners/${card.discordId}/${discordProfile.banner}.png?size=600`}
              alt=""
              objectPosition={form?.bannerObjectPosition || card.bannerObjectPosition || undefined}
              className="h-full w-full object-cover opacity-70"
            />
          </div>
        )}
        {editing && discordProfile?.banner && (
          <div className="-mx-6 sm:-mx-10 mt-2">
            <FocalPointPicker
              src={`https://cdn.discordapp.com/banners/${card.discordId}/${discordProfile.banner}.png?size=600`}
              aspect={7}
              value={form?.bannerObjectPosition}
              onChange={pos => setForm(f => (f ? { ...f, bannerObjectPosition: pos } : f))}
              label="Banner focal point — drag to reframe"
            />
          </div>
        )}
        {!discordProfile?.banner && discordProfile?.bannerColor && (
          <div
            className="-mx-6 sm:-mx-10 mt-4 h-2"
            style={{ backgroundColor: discordProfile.bannerColor }}
          />
        )}

        {/* Hero */}
        <div className="mt-8 mb-8 flex items-center gap-6">
          <div className="relative flex h-28 w-28 shrink-0 items-center justify-center">
            <div className="plate-sm absolute inset-0" />
            {discordProfile?.avatar ? (
              <img
                src={`https://cdn.discordapp.com/avatars/${card.discordId}/${discordProfile.avatar}.png?size=256`}
                alt=""
                className="relative h-full w-full rounded-md object-cover"
              />
            ) : (
              <span className="relative font-display text-6xl font-black leading-none tracking-tight text-accent-500">
                {monogram}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-ink-meta">
              Player
            </p>
            {!editing ? (
              <>
                <h1 className="mt-1 flex min-w-0 flex-wrap items-center gap-3 font-display text-3xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-4xl md:text-5xl">
                  <span className="min-w-0 max-w-full truncate">{displayName}</span>
                  {discordProfile?.discordClanTag && (
                    <span className="shrink-0 rounded bg-white/[0.06] px-2 py-1 font-mono text-[11px] font-bold uppercase tracking-wider text-ink-secondary">
                      {discordProfile.discordClanTag}
                    </span>
                  )}
                </h1>
                {discordProfile?.globalName && discordProfile.globalName !== displayName && (
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-ink-meta">
                    Discord: {discordProfile.globalName}
                  </p>
                )}
                {card.gamertag && (
                  <p className="mt-2 font-mono text-xs uppercase tracking-widest text-ink-secondary">
                    {card.gamertag}
                  </p>
                )}
              </>
            ) : form && (
              <div className="mt-2 space-y-2">
                <label className="block">
                  <span className="mb-1 block font-mono text-[9px] uppercase tracking-widest text-iron-500">
                    Display name
                  </span>
                  <input
                    value={form.displayName}
                    onChange={e => setForm({ ...form, displayName: e.target.value })}
                    placeholder={card.discordUsername ?? 'Player'}
                    maxLength={50}
                    className="input w-full"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-iron-500">
                    Gamertag (e.g. YourName#1234)
                    {gamertagLocked && <Lock className="h-2.5 w-2.5 shrink-0" strokeWidth={3} />}
                  </span>
                  <input
                    value={form.gamertag}
                    onChange={e => setForm({ ...form, gamertag: e.target.value })}
                    placeholder="Activision ID"
                    maxLength={50}
                    disabled={gamertagLocked}
                    className="input w-full disabled:cursor-not-allowed disabled:opacity-60"
                  />
                  {gamertagLocked ? (
                    <p className="mt-1 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-ink-meta">
                      <Lock className="h-2.5 w-2.5 shrink-0" strokeWidth={3} />
                      Locked — your Activision ID can't be changed once set. Ask an admin if it's wrong.
                    </p>
                  ) : (
                    <p className="mt-1 font-mono text-[9px] uppercase tracking-widest text-ink-meta">
                      Set this carefully — once saved, your Activision ID locks and only an admin can change it.
                    </p>
                  )}
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Bio */}
        <div className="mb-8">
          <p className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-ink-meta">
            Bio
          </p>
          {!editing ? (
            card.description ? (
              <p className="whitespace-pre-wrap font-mono text-sm leading-relaxed text-ink-body">
                {card.description}
              </p>
            ) : (
              <p className="font-mono text-[10px] uppercase tracking-widest text-ink-meta">
                No bio yet — click edit to add one.
              </p>
            )
          ) : form && (
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Tell the forge about yourself."
              maxLength={500}
              rows={4}
              className="input w-full resize-y"
            />
          )}
          {editing && form && (
            <p className="mt-1 text-right font-mono text-[9px] uppercase tracking-widest text-iron-500">
              {form.description.length}/500
            </p>
          )}
        </div>

        {/* Theme accent override — drives the WHOLE-APP palette */}
        {editing && form && (
          <div className="mb-8">
            <p className="mb-1 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-ink-meta">
              Theme Accent
            </p>
            <p className="mb-3 font-mono text-[10px] text-ink-meta">
              Pick a colour and the whole app shifts to it. Overrides your Discord profile colour. Leave blank to use Discord's.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {ACCENT_PRESETS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, accentOverride: c })}
                  title={c}
                  className={cn('h-8 w-8 rounded-md transition-all', form.accentOverride.toLowerCase() === c && 'ring-2 ring-accent-500 ring-offset-2 ring-offset-paper-elevated')}
                  style={{ backgroundColor: c }}
                />
              ))}
              <button
                type="button"
                onClick={() => setForm({ ...form, accentOverride: '' })}
                className={cn('flex h-8 items-center rounded-md bg-white/[0.06] px-2 font-mono text-[10px] uppercase tracking-widest transition-colors', form.accentOverride === '' ? 'text-accent-500' : 'text-ink-secondary hover:text-ink-primary')}
                title="Clear — use Discord accent"
              >
                Clear
              </button>
              <input
                value={form.accentOverride}
                onChange={e => setForm({ ...form, accentOverride: e.target.value })}
                placeholder="#5b0f6e"
                maxLength={7}
                className="input w-32 font-mono"
              />
            </div>
          </div>
        )}

        {/* Social links */}
        <div className="mb-8">
          <p className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-ink-meta">
            Social
          </p>
          {!editing ? (
            (() => {
              const links = card.socialLinks;
              const items: { label: string; value: string }[] = [];
              if (links?.tiktok) items.push({ label: 'TikTok', value: links.tiktok });
              if (links?.youtube) items.push({ label: 'YouTube', value: links.youtube });
              if (links?.discord) items.push({ label: 'Discord', value: links.discord });
              if (links?.twitter) items.push({ label: 'Twitter', value: links.twitter });
              if (links?.twitch) items.push({ label: 'Twitch', value: links.twitch });
              if (items.length === 0) {
                return (
                  <p className="font-mono text-[10px] uppercase tracking-widest text-ink-meta">
                    No social links set.
                  </p>
                );
              }
              return (
                <div className="space-y-1.5">
                  {items.map(it => (
                    <div key={it.label} className="flex items-center gap-3 rounded-lg bg-white/[0.04] px-3 py-2">
                      <span className="min-w-[6ch] font-display text-[10px] font-black uppercase tracking-[0.2em] text-ink-secondary">
                        {it.label}
                      </span>
                      <span className="truncate font-mono text-xs text-ink-body">{it.value}</span>
                    </div>
                  ))}
                </div>
              );
            })()
          ) : form && (
            <div className="space-y-2">
              {(['tiktok', 'youtube', 'discord', 'twitter', 'twitch'] as const).map(key => {
                const placeholders: Record<typeof key, string> = {
                  tiktok: 'https://tiktok.com/@your-handle',
                  youtube: 'https://youtube.com/@your-channel',
                  discord: 'username or full URL',
                  twitter: 'https://twitter.com/your-handle',
                  twitch: 'https://twitch.com/your-channel',
                };
                return (
                  <label key={key} className="block">
                    <span className="mb-1 block font-mono text-[9px] uppercase tracking-widest text-iron-500">
                      {key}
                    </span>
                    <input
                      value={form[key]}
                      onChange={e => setForm({ ...form, [key]: e.target.value })}
                      placeholder={placeholders[key]}
                      maxLength={200}
                      className="input"
                    />
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Lifetime stats — view-only */}
        {!editing && (
          <div className="mb-8">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-ink-meta">
                Lifetime
              </p>
              {card.stats && card.stats.matchesPlayed > 0 && (
                <div className="inline-flex rounded-full bg-white/[0.04] p-0.5">
                  {(['total', 'avg'] as const).map(v => (
                    <button
                      key={v}
                      onClick={() => setStatView(v)}
                      className={cn(
                        'rounded-full px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.15em] transition-colors',
                        statView === v ? 'bg-white/[0.10] text-white' : 'text-ink-meta hover:text-ink-secondary'
                      )}
                    >
                      {v === 'total' ? 'Total' : 'Per game'}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {!card.stats || card.stats.matchesPlayed === 0 ? (
              <div className="flex items-center gap-3 rounded-lg bg-white/[0.04] px-4 py-4">
                <Hash className="h-4 w-4 shrink-0 text-ink-meta" strokeWidth={2.5} />
                <p className="font-mono text-[10px] uppercase tracking-widest text-ink-meta">
                  Forge is cold — no matches recorded yet.
                </p>
              </div>
            ) : (
              <>
                {/* Headline grid — only render a tile when the value is present */}
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Stat label="K/D" value={card.stats.overallKd > 0 ? card.stats.overallKd.toFixed(2) : '—'} highlight />
                  <Stat label="Matches" value={card.stats.matchesPlayed.toLocaleString()} />
                  <Stat label="Games" value={card.stats.gamesPlayed.toLocaleString()} />
                  <Stat label="Elims" value={statDisplay(card.stats.totalEliminations, card.stats.gamesPlayed, statView)} />
                  <Stat label="Deaths" value={statDisplay(card.stats.totalDeaths, card.stats.gamesPlayed, statView)} />
                  {card.stats.totalScore != null && (
                    <Stat label="Score" value={statDisplay(card.stats.totalScore, card.stats.gamesPlayed, statView)} />
                  )}
                  {card.stats.totalObjScore != null && (
                    <Stat label="Obj Score" value={statDisplay(card.stats.totalObjScore, card.stats.gamesPlayed, statView)} />
                  )}
                  {card.stats.totalOverloads != null && (
                    <Stat label="Overloads" value={statDisplay(card.stats.totalOverloads, card.stats.gamesPlayed, statView)} />
                  )}
                  {card.stats.totalTime != null && (
                    <Stat label="Time" value={statDisplay(card.stats.totalTime, card.stats.gamesPlayed, statView, 'time')} />
                  )}
                </div>
                {/* Best-of badges */}
                {(card.stats.bestKd != null || card.stats.bestEliminations != null) && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {card.stats.bestKd != null && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-ink-secondary">
                        Best K/D · {card.stats.bestKd.toFixed(2)}
                      </span>
                    )}
                    {card.stats.bestEliminations != null && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-ink-secondary">
                        Best Elims · {card.stats.bestEliminations.toLocaleString()}
                      </span>
                    )}
                  </div>
                )}
                {/* Per-mode breakdown */}
                {card.stats.modeStats && Object.keys(card.stats.modeStats).length > 0 && (
                  <div className="mt-4">
                    <p className="mb-2 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-ink-meta">
                      By Mode
                    </p>
                    <div className="space-y-1">
                      {Object.entries(card.stats.modeStats).map(([mode, ms]) => (
                        <ModeRow key={mode} mode={mode} stats={ms} view={statView} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Discord enrichment / role badge */}
        {!editing && card.discordUsername && (
          <div className="mb-8 flex items-center gap-3 rounded-lg bg-white/[0.04] px-3 py-2">
            <Crown className="h-3.5 w-3.5 shrink-0 text-ink-secondary" strokeWidth={2.5} />
            <span className="font-mono text-[10px] uppercase tracking-widest text-ink-secondary">
              Discord · @{card.discordUsername}
            </span>
          </div>
        )}

        {/* Edit-mode footer — dirty-gated; the header's Cancel (always visible
            while editing, above) already covers discard-and-close, so this
            bar only needs Save. Reuses diffFormState — the existing
            initial-vs-current diff this form already computes on submit — as
            the dirty check, no parallel state invented. */}
        {editing && (
          <div className="pt-5">
            <SaveBar
              dirty={dirty}
              saving={mutation.isPending}
              saved={savedFlash}
              error={formError}
              onSave={onSave}
              savingLabel="Forging…"
            />
          </div>
        )}
      </article>
    </PageWrapper>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Per-game average = total ÷ games (0 when no games). Lets the card show both
// cumulative totals and per-game averages from the same data, user-toggled.
function perGame(total: number, games: number): number {
  return games > 0 ? total / games : 0;
}

// Format a stat for the headline grid / mode rows in the chosen view.
// kind 'time' formats as a duration; otherwise toLocaleString (avg keeps 1 dp).
function statDisplay(total: number, games: number, view: 'total' | 'avg', kind: 'num' | 'time' = 'num'): string {
  const v = view === 'avg' ? perGame(total, games) : total;
  if (kind === 'time') return formatDuration(Math.round(v));
  return view === 'avg' ? v.toLocaleString(undefined, { maximumFractionDigits: 1 }) : v.toLocaleString();
}

/** Seconds → "Hh Mm" for large values, "m:ss" for smaller ones. */
function formatDuration(seconds: number): string {
  if (seconds <= 0) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Title-case a mode key, with known friendly aliases. */
function modeLabel(key: string): string {
  const known: Record<string, string> = {
    hardpoint: 'Hardpoint',
    snd: 'SND',
    overload: 'Overload',
  };
  return known[key.toLowerCase()] ?? key.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function ModeRow({ mode, stats, view }: { mode: string; stats: MyCardModeStats; view: 'total' | 'avg' }) {
  const g = stats.gamesPlayed;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-white/[0.03] px-3 py-2">
      <span className="w-20 shrink-0 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-ink-secondary">
        {modeLabel(mode)}
      </span>
      <ModeCell label="Games" value={stats.gamesPlayed.toLocaleString()} />
      <ModeCell label="K/D" value={stats.kd > 0 ? stats.kd.toFixed(2) : '—'} accent />
      <ModeCell label="Elims" value={statDisplay(stats.totalEliminations, g, view)} />
      <ModeCell label="Deaths" value={statDisplay(stats.totalDeaths, g, view)} />
      <ModeCell label="Score" value={statDisplay(stats.totalScore, g, view)} />
      <ModeCell label="Obj" value={statDisplay(stats.totalObjectiveScore, g, view)} />
      {stats.totalOverloads != null && (
        <ModeCell label="Overloads" value={statDisplay(stats.totalOverloads, g, view)} />
      )}
      {stats.totalTime != null && (
        <ModeCell label="Time" value={statDisplay(stats.totalTime, g, view, 'time')} />
      )}
    </div>
  );
}

function ModeCell({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <span className="flex flex-col items-center">
      <span className="font-mono text-[8px] uppercase tracking-widest text-ink-meta">{label}</span>
      <span className={`font-mono text-xs font-bold tabular-nums ${accent ? 'text-accent-500' : 'text-white'}`}>
        {value}
      </span>
    </span>
  );
}

// ─── Stat tile ────────────────────────────────────────────────────────────────

function Stat({ label, value, highlight = false }: { label: string; value: string | number; highlight?: boolean }) {
  return (
    <div className="plate-sm flex flex-col items-center justify-center gap-1 px-3 py-5 text-center">
      <p className="font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-ink-meta">
        {label}
      </p>
      <p className={`font-display text-3xl font-black tabular-nums leading-none ${highlight ? 'text-accent-500' : 'text-white'}`}>
        {value}
      </p>
    </div>
  );
}
